import { useEffect, useMemo, useState } from "react";
import { Search, Loader2, Crown, UserMinus, UserPlus, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { UserAvatar } from "@/components/UserBits";
import { GoldCheck } from "@/components/GoldCheck";
import { SupportPromptPush } from "@/components/SupportPromptPush";

const PRESETS = [7, 30, 90, 365];
const active = (u) => u.supporter && (!u.supporter_until || new Date(u.supporter_until) > new Date());
const until = (u) => (u.supporter_until ? `bis ${new Date(u.supporter_until).toLocaleDateString("de-DE")}` : "unbegrenzt");

function Row({ u, days, busy, onSet }) {
  const on = active(u);
  return (
    <li className={`flex flex-wrap items-center gap-3 p-3 rounded-2xl border ${on ? "vip-row border-amber-200" : "border-pink-50"}`} data-testid={`supporter-admin-row-${u.user_id}`}>
      <UserAvatar userId={u.user_id} name={u.name} picture={u.picture} size="w-10 h-10" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm truncate flex items-center gap-1">{u.name}{on && <GoldCheck className="w-4 h-4" />}</p>
        <p className="text-xs text-slate-400 truncate">{on ? `Premium ${until(u)}` : u.email}</p>
      </div>
      <div className="flex gap-2 justify-end w-full sm:w-auto">
      {on && <button disabled={busy} onClick={() => onSet(u, true)} data-testid={`supporter-admin-extend-${u.user_id}`} title={`Um ${days} Tage ab heute verlängern`} className="shrink-0 h-9 px-3 rounded-full border border-amber-300 text-amber-700 text-xs font-bold hover:bg-amber-50 inline-flex items-center gap-1 disabled:opacity-50"><CalendarPlus className="w-3.5 h-3.5" /> {days} T.</button>}
      <button disabled={busy} onClick={() => onSet(u, !on)} data-testid={`supporter-admin-toggle-${u.user_id}`}
        className={`shrink-0 h-9 px-3 rounded-full text-xs font-extrabold inline-flex items-center gap-1 disabled:opacity-50 ${on ? "bg-slate-100 text-slate-600 hover:bg-red-50 hover:text-red-600" : "vip-gold-metal"}`}>
        {on ? <><UserMinus className="w-3.5 h-3.5" /> Wegnehmen</> : <><UserPlus className="w-3.5 h-3.5" /> {days ? `${days} Tage` : "Tage?"}</>}
      </button>
      </div>
    </li>
  );
}

export function SupporterAdmin() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);
  const supporters = useMemo(() => (users || []).filter(active), [users]);
  const hits = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t.length < 2 ? [] : (users || []).filter((u) => u.role !== "admin" && (u.name?.toLowerCase().includes(t) || u.email?.toLowerCase().includes(t))).slice(0, 20);
  }, [users, q]);
  const set = async (u, on) => {
    if (on && (!days || days < 1)) return toast.error("Bitte eine Tagesanzahl eingeben");
    if (!on && !window.confirm(`Premium von ${u.name} wirklich wegnehmen? Der Nutzer wird benachrichtigt.`)) return;
    if (on && active(u) && !u.supporter_until && !window.confirm(`${u.name} ist unbegrenzt Projekt Unterstützer. Wirklich auf ${days} Tage ab heute ändern?`)) return;
    setBusy(true);
    try {
      await api.put(`/admin/users/${u.user_id}/supporter`, on ? { supporter: true, days: Number(days) } : { supporter: false });
      toast.success(on ? `${u.name}: Premium für ${days} Tage aktiv – Nutzer wurde benachrichtigt` : `${u.name}: Premium entfernt – Nutzer wurde benachrichtigt`);
      await load();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-3xl p-5 sm:p-6 pb-20 sm:pb-6 border border-pink-100 space-y-5" data-testid="admin-supporters">
      <div>
        <h3 className="font-display text-lg font-extrabold flex items-center gap-2"><Crown className="w-5 h-5 text-amber-500" fill="currentColor" /> Projekt Unterstützer verwalten</h3>
        <p className="text-sm text-slate-500">Nutzer suchen und per Klick Premium geben oder wegnehmen. Nur der Nutzer bekommt eine Mitteilung.</p>
      </div>
      <SupportPromptPush />
      <div className="flex flex-wrap items-center gap-2" data-testid="supporter-admin-days">
        <span className="text-sm font-semibold">Laufzeit:</span>
        {PRESETS.map((d) => <button key={d} onClick={() => setDays(d)} data-testid={`supporter-admin-days-${d}`} className={`h-8 px-3 rounded-full text-xs font-bold ${days === d ? "pink-gradient text-white" : "bg-rose-50 text-rose-600"}`}>{d} Tage</button>)}
        <label className="flex items-center gap-1.5 text-xs text-slate-500">eigene:
          <input type="number" min={1} max={3650} value={days} onChange={(e) => setDays(Math.max(0, Math.min(3650, Number(e.target.value) || 0)))} data-testid="supporter-admin-days-input" className="w-20 h-8 px-2 rounded-lg border border-pink-200 text-base sm:text-sm" /> Tage
        </label>
      </div>
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nutzer suchen (Name oder E-Mail) …" data-testid="supporter-admin-search" className="w-full h-11 pl-9 pr-3 rounded-full border border-pink-200 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-rose-300" />
      </div>
      {users === null ? <Loader2 className="w-5 h-5 animate-spin text-rose-400" /> : <>
        {q.trim().length >= 2 && (
          <ul className="space-y-2" data-testid="supporter-admin-results">
            {hits.length === 0 ? <li className="text-sm text-slate-400">Kein Nutzer gefunden</li> : hits.map((u) => <Row key={u.user_id} u={u} days={days} busy={busy} onSet={set} />)}
          </ul>
        )}
        <div className="space-y-2">
          <p className="text-xs font-extrabold uppercase tracking-widest text-amber-600" data-testid="supporter-admin-count">Aktive Projekt Unterstützer ({supporters.length})</p>
          {supporters.length === 0 ? <p className="text-sm text-slate-400">Noch keine aktiven Unterstützer</p>
            : <ul className="space-y-2">{supporters.map((u) => <Row key={u.user_id} u={u} days={days} busy={busy} onSet={set} />)}</ul>}
        </div>
      </>}
    </div>
  );
}
